<template>
  <app-page title="Invoices" subtitle="View your off cycle invoices" icon="receipt">
    <navigable-tabs>
      <b-tab :id="TAB_NAMES.UNPAID">
        <template #title>
          <h4>Unpaid ({{ unpaidInvoices.length }})</h4>
        </template>
        <div class="pt-4 px-2">
          <b-table class="invoices-table" :fields="UNPAID_FIELDS" :items="unpaidInvoices" table-class="table-minimal">
            <template #cell(action)="data">
              <div class="go-to" @click="downloadInvoice(data?.item)" :id="`invoice-${data?.item?.invoice_id}`">
                <b-spinner v-if="isDownloading === data?.item?.invoice_id" class="download-spinner" variant="primary" />
                <Download :color="PaletteColors['brand-primary']" v-else />
              </div>
            </template>
            <template #cell(state)="data">
              <div :class="isPast(data?.item?.due_on) ? 'overdue' : 'open'">
                {{ isPast(data?.item?.due_on) ? 'OVERDUE' : data?.item?.state }}
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>

      <b-tab :id="TAB_NAMES.PAID">
        <template #title>
          <h4>Paid ({{ paidInvoices.length }})</h4>
        </template>
        <div class="pt-4 px-2">
          <b-table class="invoices-table" :fields="PAID_FIELDS" :items="paidInvoices" table-class="table-minimal">
            <template #cell(action)="data">
              <div class="go-to" @click="downloadInvoice(data?.item)" :id="`invoice-${data?.item?.invoice_id}`">
                <b-spinner v-if="isDownloading === data?.item?.invoice_id" class="download-spinner" variant="primary" />
                <Download :color="PaletteColors['brand-primary']" v-else />
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>
    </navigable-tabs>
  </app-page>
</template>

<script setup>
import Download from '@/assets/icons/Download.vue';
import AppPage from '@/components/AppPage.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import { ref, watch } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { getOffCycleInvoices, downloadOffCycleInvoice } from '@/queries/one-time-payments';
import { toCurrency } from '@/utils';
import { PaletteColors } from '@/Theme';
import { format, isPast } from 'date-fns';

const TAB_NAMES = {
  UNPAID: 'unpaid',
  PAID: 'paid',
};

const getInvoicesQuery = useQuery(getOffCycleInvoices());
const downloadInvoiceMutation = useMutation(downloadOffCycleInvoice());

watch([getInvoicesQuery.fetchStatus, getInvoicesQuery.isPending], () => {
  if (getInvoicesQuery.data.value) {
    unpaidInvoices.value = getInvoicesQuery.data.value
      .filter(invoice => invoice.state !== 'PAID')
      .sort((a, b) => new Date(b.issued_on) - new Date(a.issued_on));
    paidInvoices.value = getInvoicesQuery.data.value
      .filter(invoice => invoice.state === 'PAID')
      .sort((a, b) => new Date(b.issued_on) - new Date(a.issued_on));
  }
});

const unpaidInvoices = ref([]);
const paidInvoices = ref([]);
const isDownloading = ref('');

watch(downloadInvoiceMutation.isPending, isPending => {
  if (isPending) {
    isDownloading.value = downloadInvoiceMutation?.variables?.value;
  } else {
    isDownloading.value = '';
  }
});

const UNPAID_FIELDS = [
  {
    key: 'invoice_number',
    label: 'Invoice Number',
  },
  {
    key: 'issued_on',
    label: 'Issued on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'due_on',
    label: 'Due on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'state',
    label: 'State',
    tdClass: 'text-center',
    thClass: 'text-center',
  },
  {
    key: 'amount',
    label: 'Amount',
    formatter: value => toCurrency(value, 'USD'),
    tdClass: 'text-right',
    thStyle: { textAlign: 'right' },
  },
  {
    key: 'action',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

const PAID_FIELDS = [
  {
    key: 'invoice_number',
    label: 'Invoice Number',
  },
  {
    key: 'issued_on',
    label: 'Issued on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'paid_on',
    label: 'Paid on',
    formatter: value => formatDateForTable(value),
  },
  {
    key: 'amount',
    label: 'Amount',
    formatter: value => toCurrency(value, 'USD'),
  },
  {
    key: 'action',
    label: '',
    thStyle: { width: '20px' },
    tdStyle: { width: '20px' },
  },
];

const downloadInvoice = invoice => {
  if (downloadInvoiceMutation.isPending.value) {
    return;
  }

  const invoiceId = invoice.invoice_id;

  downloadInvoiceMutation.mutate(invoiceId, {
    onSuccess: async response => {
      const blob = new Blob([response], { type: 'application/pdf' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.append(link);
      link.click();
      link.remove();
    },
    onError: error => {
      console.error('Error downloading invoice:', error);
    },
  });
};

const formatDateForTable = date => {
  if (!date) {
    return '';
  }
  return format(new Date(date), 'dd MMM yyyy');
};
</script>

<style scoped>
.go-to {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    cursor: pointer;

    svg {
      fill: var(--palette-color-brand-primary);
    }
  }
}

.invoices-table {
  background-color: var(--palette-color-base-white);
  border-radius: 0.5rem;
  padding: 2rem;
  width: 100%;
}

.overdue {
  color: var(--palette-color-danger);
  background-color: var(--palette-color-danger-lighten-90);
  padding: 0.5rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: bold;
}

.open {
  color: var(--palette-color-warning);
  background-color: var(--palette-color-warning-lighten-90);
  padding: 0.5rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: bold;
}

.download-spinner {
  width: 1.5rem;
  height: 1.5rem;
}
</style>
